import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, CalendarX, CheckCircle2, CirclePause, DoorClosed, Link2, Link2Off,
  LogIn, RefreshCw, Shuffle, WifiOff,
} from "lucide-react";
import { AppButton, FormField, Input, cn } from "../../ui";
import { codeFromInput, statusCopy } from "./joinInvite";

const STATUS_ICONS = {
  INVITE_MISSING: Link2,
  INVITE_INVALID: Link2Off,
  INVITE_DISABLED: CirclePause,
  INVITE_EXPIRED: CalendarX,
  INSTITUTE_CLOSED: DoorClosed,
  INVITE_OTHER_INSTITUTE: Shuffle,
  NETWORK: WifiOff,
  ACCOUNT_CREATED: CheckCircle2,
};

// The tint and the ink readable on it, per tone — the `-soft` / `-fg` pairs.
const STATUS_TONE_CLASSES = {
  info: "tw-bg-info-soft tw-text-info-fg",
  warning: "tw-bg-warning-soft tw-text-warning-fg",
  danger: "tw-bg-danger-soft tw-text-danger-fg",
  success: "tw-bg-success-soft tw-text-success-fg",
};

/**
 * The card's content when there is no form to show: a link that is missing,
 * mangled, switched off or expired, an institute not taking students, a
 * network failure, or an account made by a server too old to sign the student
 * in. Says what happened, what to do, and — where the link itself may be the
 * problem — takes the link or the code again.
 *
 *   status      a key of INVITE_STATUSES
 *   brandName   the inviting institute, when known
 *   onRetry     "Try again" (NETWORK)
 *   onCode      (code) => open the page for a code typed or pasted here
 */
const InviteStatus = ({ status, brandName, onRetry, onCode }) => {
  const copy = statusCopy(status, brandName);
  const Icon = STATUS_ICONS[status] || Link2Off;
  const [pasted, setPasted] = useState("");
  const [pasteError, setPasteError] = useState("");

  const submitCode = (e) => {
    e.preventDefault();
    const code = codeFromInput(pasted);
    if (!code) {
      setPasteError("Paste the invite link, or type the code from it.");
      return;
    }
    onCode(code);
  };

  return (
    <div className="tw-flex tw-flex-col tw-items-center tw-text-center">
      <span
        aria-hidden="true"
        className={cn(
          "tw-mb-4 tw-inline-flex tw-h-14 tw-w-14 tw-items-center tw-justify-center tw-rounded-full",
          STATUS_TONE_CLASSES[copy.tone] || STATUS_TONE_CLASSES.info
        )}
      >
        <Icon size={26} />
      </span>

      <h1 className="tw-mb-2 tw-text-[1.375rem] tw-font-bold tw-leading-tight tw-tracking-[-0.01em] tw-text-content sm:tw-text-2xl">
        {copy.title}
      </h1>
      <p className="tw-mb-0 tw-max-w-[26rem] tw-text-sm tw-leading-relaxed tw-text-content-muted">
        {copy.body}
      </p>

      {copy.askForCode && (
        <form
          onSubmit={submitCode}
          noValidate
          className="tw-mt-6 tw-flex tw-w-full tw-flex-col tw-gap-3 tw-text-left"
        >
          <FormField label="Invite link or code" error={pasteError || null}>
            <Input
              size="lg"
              value={pasted}
              onChange={(e) => {
                setPasted(e.target.value);
                setPasteError("");
              }}
              placeholder="Paste the link, or a code like a1b2c3d4e5"
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              leading={<Link2 size={16} />}
            />
          </FormField>
          <AppButton type="submit" size="lg" className="tw-h-11 tw-w-full">
            Continue <ArrowRight size={16} />
          </AppButton>
        </form>
      )}

      {copy.retry && (
        <AppButton size="lg" className="tw-mt-6 tw-h-11 tw-w-full" onClick={onRetry}>
          <RefreshCw size={16} /> Try again
        </AppButton>
      )}

      {copy.signIn ? (
        <AppButton as={Link} to="/login" size="lg" className="tw-mt-6 tw-h-11 tw-w-full tw-no-underline">
          <LogIn size={16} /> Sign in
        </AppButton>
      ) : (
        <p className="tw-mb-0 tw-mt-6 tw-text-sm tw-text-content-muted">
          Already have an account?{" "}
          <Link to="/login" className="tw-font-semibold tw-text-primary tw-no-underline hover:tw-underline">
            Sign in
          </Link>
        </p>
      )}
    </div>
  );
};

export default InviteStatus;
